/**
 * @file CreateEmployeeDialog.tsx
 * @brief Sheet form to invite an employee via POST /api/company/users.
 *
 * @author DES
 * @date 19-06-2026
 * @updated 29-07-2026 — FormSheet + GridBlock chrome
 * @updated 01-10-2026 — form standard: palette frame and the same Profile /
 *   Access / Security tabs as Edit employee
 */

import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Eye, EyeOff, KeyRound, LoaderCircle, ShieldCheck, UserRound } from "lucide-react";
import { FormSheetTabs, formSheetTabPanel, type FormSheetTab } from "@/components/ui/FormSheetTabs";
import { Button } from "@/components/ui/Button";
import {
  FormFieldGroup,
  FormSheet,
  FormSheetAlert,
  FormSheetBody,
  FormSheetContent,
  FormSheetDescription,
  FormSheetField,
  FormSheetFields,
  FormSheetFooter,
  FormSheetFooterCell,
  FormSheetFrame,
  FormSheetHeader,
  FormSheetSection,
  FormSheetTitle,
  formSheetActionClass,
} from "@/components/ui/FormSheet";
import { Input } from "@/components/ui/Input";
import { TitlePicker } from "@/components/ui/TitlePicker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import { ApiClientError } from "@/shared/api/client";
import { toast } from "@/shared/hooks/useToast";
import { cn } from "@/shared/theme/cn";
import { useCreateEmployee } from "../api";
import {
  CreateEmployeeInputSchema,
  type CreateEmployeeInput,
} from "../schemas";

interface CreateEmployeeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateEmployeeDialog({
  open,
  onOpenChange,
}: CreateEmployeeDialogProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  // Tabs, as Edit employee (DES 01-10-2026: form standard).
  const [tab, setTab] = useState<CreateEmployeeTab>("profile");
  const create = useCreateEmployee();

  const form = useForm<CreateEmployeeInput>({
    resolver: zodResolver(CreateEmployeeInputSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      title: "",
      role: "user",
    },
  });

  useEffect(() => {
    if (open) {
      form.reset();
      setShowPassword(false);
      setServerError(null);
      setTab("profile");
    }
  }, [open, form]);

  const onSubmit = form.handleSubmit(
    async (values) => {
      setServerError(null);
      try {
        await create.mutateAsync({
          ...values,
          title: values.title?.trim() || undefined,
          phoneNumber: values.phoneNumber?.trim() || undefined,
        });
        toast({
          variant: "success",
          title: "Employee added",
          description: `${values.email} can now sign in.`,
        });
        onOpenChange(false);
      } catch (err) {
        const message =
          err instanceof ApiClientError ? err.message : "Please try again.";
        setServerError(message);
      }
    },
    // A field in a hidden tab failed: open the first tab with an error.
    (errs) => {
      const keys = Object.keys(errs);
      if (keys.includes("password")) setTab("security");
      if (keys.includes("role")) setTab("access");
      if (keys.some((k) => ["firstName", "lastName", "email", "title"].includes(k))) {
        setTab("profile");
      }
    },
  );

  const errors = form.formState.errors;

  return (
    <FormSheet open={open} onOpenChange={onOpenChange}>
      {/* Opens like Cmd+K, as Edit employee (DES 01-10-2026: form standard). */}
      <FormSheetContent side="palette">
        <form
          onSubmit={onSubmit}
          className="flex min-h-0 flex-1 flex-col overflow-hidden"
          noValidate
        >
          <FormSheetFrame>
            <FormSheetHeader>
              <FormSheetTitle>Add employee</FormSheetTitle>
              <FormSheetDescription>
                Creates an account in your company directory.
              </FormSheetDescription>
            </FormSheetHeader>

            <FormSheetTabs
              tabs={CREATE_EMPLOYEE_TABS}
              value={tab}
              onChange={setTab}
              label="Add employee"
            />

            <FormSheetBody>
              {/* Every tab stays mounted, so values and errors survive
                  switching tabs; hidden panels are display:none. */}
              <div {...formSheetTabPanel(tab, "profile")}>
                <FormSheetSection
                  hideHeading
                  title="Profile"
                  subtitle="Identity details for the new team member."
                >
                  {/* Title, first and last name in one row, then email, as
                      Edit user (DES 01-10-2026: form standard). */}
                  <FormSheetFields className="sm:grid-cols-3">
                    <FormSheetField>
                      <FormFieldGroup label="Title (optional)" error={errors.title?.message}>
                        <TitlePicker
                          value={form.watch("title") || null}
                          onChange={(label) =>
                            form.setValue("title", label ?? "")
                          }
                        />
                      </FormFieldGroup>
                    </FormSheetField>
                    <FormSheetField>
                      <FormFieldGroup
                        label="First name"
                        htmlFor="emp-first"
                        required
                        error={errors.firstName?.message}
                      >
                        <Input capitalizeFirst id="emp-first" {...form.register("firstName")} />
                      </FormFieldGroup>
                    </FormSheetField>
                    <FormSheetField>
                      <FormFieldGroup
                        label="Last name"
                        htmlFor="emp-last"
                        required
                        error={errors.lastName?.message}
                      >
                        <Input capitalizeFirst id="emp-last" {...form.register("lastName")} />
                      </FormFieldGroup>
                    </FormSheetField>
                    <FormSheetField span={2}>
                      <FormFieldGroup
                        label="Email"
                        htmlFor="emp-email"
                        required
                        error={errors.email?.message}
                      >
                        <Input
                          id="emp-email"
                          type="email"
                          {...form.register("email")}
                        />
                      </FormFieldGroup>
                    </FormSheetField>
                  </FormSheetFields>
                </FormSheetSection>
              </div>

              <div {...formSheetTabPanel(tab, "access")}>
                <FormSheetSection
                  hideHeading
                  title="Access"
                  subtitle="Role controls what they can manage in the company panel."
                >
                  <FormSheetFields className="sm:grid-cols-3">
                    <FormSheetField>
                      <FormFieldGroup label="Role" required error={errors.role?.message}>
                        <Select
                          value={form.watch("role")}
                          onValueChange={(v) =>
                            form.setValue(
                              "role",
                              v as CreateEmployeeInput["role"],
                            )
                          }
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="user">User</SelectItem>
                            <SelectItem value="admin">Company Admin</SelectItem>
                          </SelectContent>
                        </Select>
                      </FormFieldGroup>
                    </FormSheetField>
                  </FormSheetFields>
                </FormSheetSection>
              </div>

              <div {...formSheetTabPanel(tab, "security")}>
                <FormSheetSection
                  hideHeading
                  title="Security"
                  subtitle="They can change this after first sign-in."
                >
                  <FormSheetFields>
                    <FormSheetField>
                      <FormFieldGroup
                        label="Temporary password"
                        htmlFor="emp-password"
                        required
                        error={errors.password?.message}
                      >
                        <div className="relative">
                          <Input
                            id="emp-password"
                            type={showPassword ? "text" : "password"}
                            className="pr-10"
                            {...form.register("password")}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="absolute right-1 top-1/2 h-8 w-8 -translate-y-1/2"
                            onClick={() => setShowPassword((v) => !v)}
                            aria-label={
                              showPassword ? "Hide password" : "Show password"
                            }
                          >
                            {showPassword ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                      </FormFieldGroup>
                    </FormSheetField>
                  </FormSheetFields>
                </FormSheetSection>
              </div>

              {serverError ? (
                <FormSheetAlert>{serverError}</FormSheetAlert>
              ) : null}
            </FormSheetBody>

            <FormSheetFooter>
              <FormSheetFooterCell>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => onOpenChange(false)}
                  disabled={create.isPending}
                  className={formSheetActionClass}
                >
                  Cancel
                </Button>
              </FormSheetFooterCell>
              <FormSheetFooterCell>
                <Button
                  type="submit"
                  disabled={create.isPending}
                  className={cn(
                    formSheetActionClass,
                    "bg-primary text-primary-foreground hover:bg-primary/90",
                  )}
                >
                  {create.isPending ? (
                    <>
                      <LoaderCircle className="h-4 w-4 animate-spin" /> Adding…
                    </>
                  ) : (
                    "Add employee"
                  )}
                </Button>
              </FormSheetFooterCell>
            </FormSheetFooter>
          </FormSheetFrame>
        </form>
      </FormSheetContent>
    </FormSheet>
  );
}

type CreateEmployeeTab = "profile" | "access" | "security";

// Same tabs and icons as Edit employee (DES 01-10-2026: form standard).
const CREATE_EMPLOYEE_TABS: FormSheetTab<CreateEmployeeTab>[] = [
  { id: "profile", label: "Profile", icon: UserRound },
  { id: "access", label: "Access", icon: ShieldCheck },
  { id: "security", label: "Security", icon: KeyRound },
];
